/**
 * Leaderboard View
 * Displays ranked users by betting performance
 */

import { EmptyState, LoadingSpinner } from '@components/ui';
import { useLeaderboard } from '@hooks/useLeaderboard';
import type { BetsType, SortBy, TimePeriod } from '@parlay-picker/shared/hooks';
import { AlertTriangle, Info, Loader2 } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { LeaderboardFilters } from './LeaderboardFilters';
import { LeaderboardPodium } from './LeaderboardPodium';
import { LeaderboardTable } from './LeaderboardTable';
import { UserRankBanner } from './UserRankBanner';

// =============================================================================
// Component
// =============================================================================

export function LeaderboardView(): ReactElement {
  // Filter state
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('30d');
  const [betsType, setBetsType] = useState<BetsType>('all');
  const [propType, setPropType] = useState<string | undefined>(undefined);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [followingOnly, setFollowingOnly] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>('roi');
  const [minBets, setMinBets] = useState(10);

  // Load leaderboard data
  const { data, loading, error, hasMore, loadMore } = useLeaderboard({
    timePeriod,
    betsType,
    propType,
    verifiedOnly,
    followingOnly,
    sortBy,
    minBets,
  });

  return (
    <div data-testid="leaderboard-view" className="max-w-7xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold text-theme-text">Leaderboard</h1>
        <p className="text-theme-text-secondary">
          Ranked by betting performance. All stats are automatically computed from settled bets.
        </p>
      </div>

      {/* Ranking Explanation Banner */}
      {(sortBy === 'roi' || sortBy === 'winRate') && (
        <div className="bg-theme-primary/10 border border-theme-primary/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-theme-primary shrink-0 mt-0.5" />
            <div className="text-sm text-theme-primary">
              <span className="font-semibold">Fair Ranking System:</span> Rankings use adjusted
              scores to prevent users with few bets from dominating. The "raw" value shows actual
              performance, while the ranking score adjusts for sample size fairness.
            </div>
          </div>
        </div>
      )}

      {(sortBy === 'rawRoi' || sortBy === 'rawWinRate') && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-400">
              <span className="font-semibold">Raw Performance:</span> Sorted by pure stats without
              adjustment. Users with few bets may rank higher than those with more data. Use
              "Adjusted" sort for fairer comparison.
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <LeaderboardFilters
        timePeriod={timePeriod}
        onTimePeriodChange={setTimePeriod}
        betsType={betsType}
        onBetsTypeChange={setBetsType}
        propType={propType}
        onPropTypeChange={setPropType}
        verifiedOnly={verifiedOnly}
        onVerifiedOnlyChange={setVerifiedOnly}
        followingOnly={followingOnly}
        onFollowingOnlyChange={setFollowingOnly}
        sortBy={sortBy}
        onSortByChange={setSortBy}
        minBets={minBets}
        onMinBetsChange={setMinBets}
      />

      {/* User's Rank Banner */}
      {data?.userRank && <UserRankBanner userRank={data.userRank} />}

      {/* Top 3 Podium */}
      {!loading && !error && data && data.leaderboard.length >= 3 && (
        <LeaderboardPodium entries={data.leaderboard} sortBy={sortBy} />
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-theme-danger/10 border border-theme-danger/20 rounded-lg p-4 text-center">
          <p className="text-theme-danger">{error}</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && data && data.leaderboard.length === 0 && (
        <EmptyState title="No users match the selected filters" />
      )}

      {/* Leaderboard Table */}
      {!error && data && data.leaderboard.length > 0 && (
        <div className="space-y-4">
          <div className="text-sm text-theme-text-secondary text-center">
            Showing {data.leaderboard.length} of {data.totalUsers} users
          </div>
          <LeaderboardTable entries={data.leaderboard} showUnits={betsType === 'actual'} />
          {hasMore && (
            <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={loadMore}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-theme-primary hover:text-theme-primary-hover bg-theme-surface border border-theme-border rounded-lg hover:bg-theme-surface-hover transition-colors disabled:opacity-50 min-h-10"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Load more'
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
